import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { analyze, TextFeatures } from 'src/achievements/analyzer';
import { evaluate, snapshot } from 'src/achievements/engine';
import { Achievement, ACHIEVEMENT_CATALOG } from 'src/achievements/catalog';
import AchievementToast from 'src/achievements/AchievementToast';

/**
 * Standalone demo page for the Minecraft-style achievement system.
 * Lives at /achievements-demo. NO integration into the document editor —
 * this is for visually validating the UX before we wire it in.
 *
 * Type into the textarea on the left → achievements unlock in real time
 * with a top-right toast → the achievement panel on the right shows the
 * earned set (with locked silhouettes for the others).
 *
 * Useful for: testing toast timing, animation feel, visual hierarchy,
 * achievement balance (too easy? too hard?), and the secret-achievement reveal.
 */

const DEMO_SIGNAL_WORDS = [
  'at first', 'before', 'during', 'while', 'meanwhile', 'next', 'following',
  'later', 'after', 'suddenly', 'finally', 'then',
];

const SEED_TEXT = '';

// Bump on catalog changes so stale earned ids don't linger in browsers.
const STORAGE_KEY = 'pressto:achievements-demo:earned:v2';

function loadEarned(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveEarned(earned: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(earned)));
  } catch { /* noop */ }
}

export default function AchievementsDemo() {
  const [text, setText] = useState(SEED_TEXT);
  const [earnedIds, setEarnedIds] = useState<Set<string>>(() => loadEarned());
  const [celebrating, setCelebrating] = useState<Achievement[]>([]);
  const debounceRef = useRef<number | null>(null);

  // Debounced text analysis. Run ~200ms after the user stops typing.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const features = analyze(text, DEMO_SIGNAL_WORDS);
      const result = evaluate(features, earnedIds);
      if (result.newlyEarned.length > 0) {
        setCelebrating((prev) => [...prev, ...result.newlyEarned]);
        setEarnedIds(result.earned);
        saveEarned(result.earned);
      }
    }, 200);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [text]); // intentionally not depending on earnedIds — we use the latest in the effect body

  const features: TextFeatures = useMemo(
    () => analyze(text, DEMO_SIGNAL_WORDS),
    [text],
  );

  const dismiss = (id: string) => {
    setCelebrating((prev) => prev.filter((a) => a.id !== id));
  };

  const resetAll = () => {
    setEarnedIds(new Set());
    saveEarned(new Set());
    setCelebrating([]);
  };

  const earnedCount = earnedIds.size;
  const totalCount = ACHIEVEMENT_CATALOG.length;

  const cards = snapshot(features, earnedIds);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F5F3EC', py: 5, px: { xs: 2, md: 5 } }}>
      <AchievementToast achievements={celebrating} onDismiss={dismiss} />

      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography
            sx={{
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              color: '#1e1e1e',
            }}
          >
            Achievements Demo
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 800,
                fontSize: '0.9rem',
                color: '#5C4FB8',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {earnedCount} of {totalCount} earned
            </Typography>
            <Box
              component="button"
              onClick={resetAll}
              sx={{
                cursor: 'pointer',
                backgroundColor: '#FFFFFF',
                border: '2px solid #1e1e1e',
                borderRadius: '8px',
                boxShadow: '2px 2px 0px #1e1e1e',
                px: 1.5, py: 0.75,
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Reset
            </Box>
          </Box>
        </Box>

        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.95rem', color: '#555', mb: 3 }}>
          Type in the box. Watch the top-right corner — achievements unlock as you write. Earned state persists in your browser; click Reset to start over.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* Writing canvas */}
          <Box>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Start writing a real piece. Try: setting the scene (yesterday, at the park), using vivid verbs (raced, whispered), naming a feeling (nervous, thrilled), backing up an opinion with 'because…', writing dialogue ('I'm hungry,' she said), or using 'I wonder…'"
              style={{
                width: '100%',
                minHeight: '480px',
                padding: '20px',
                fontSize: '1.05rem',
                lineHeight: 1.55,
                fontFamily: 'Georgia, serif',
                backgroundColor: '#FFFFFF',
                border: '2.5px solid #1e1e1e',
                borderRadius: '12px',
                boxShadow: '5px 5px 0px #1e1e1e',
                outline: 'none',
                resize: 'vertical',
                color: '#1e1e1e',
              }}
            />
            <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap', fontFamily: 'Montserrat, sans-serif', fontSize: '0.75rem', color: '#666' }}>
              <span><strong>{features.wordCount}</strong> words</span>
              <span><strong>{features.uniqueWordCount}</strong> unique</span>
              <span><strong>{features.sentenceCount}</strong> sentences</span>
              <span><strong>{features.paragraphCount}</strong> paragraphs</span>
              <span>longest sentence: <strong>{features.longestSentenceWords}</strong> words</span>
              <span>longest word: <strong>{features.longestWordLength}</strong> letters</span>
            </Box>
          </Box>

          {/* Achievement panel */}
          <Box
            sx={{
              backgroundColor: '#FAF7F0',
              border: '2px solid #1e1e1e',
              borderRadius: '12px',
              boxShadow: '4px 4px 0px #1e1e1e',
              p: 3,
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5 }}>
              {cards.map(({ achievement, isEarned, progress }) => {
                const showSecret = achievement.isSecret && !isEarned;
                return (
                  <Box
                    key={achievement.id}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      border: `2px solid ${isEarned ? '#1e1e1e' : '#D5D5DD'}`,
                      borderRadius: '10px',
                      boxShadow: isEarned ? '3px 3px 0px #1e1e1e' : 'none',
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: 0.5,
                      opacity: isEarned ? 1 : 0.85,
                      transition: 'all 150ms',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 32,
                        lineHeight: 1,
                        filter: isEarned ? 'none' : 'grayscale(1) opacity(0.45)',
                      }}
                    >
                      {showSecret ? '🔒' : achievement.icon}
                    </Box>
                    <Box
                      sx={{
                        fontFamily: 'Georgia, serif',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: isEarned ? '#1e1e1e' : '#999',
                      }}
                    >
                      {showSecret ? 'Secret' : achievement.name}
                    </Box>
                    {!showSecret && (
                      <Box sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.65rem', color: '#666', lineHeight: 1.3 }}>
                        {achievement.description}
                      </Box>
                    )}
                    {!isEarned && progress && progress.target > 1 && (
                      <Box sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#5C4FB8', mt: 0.5 }}>
                        {progress.current} / {progress.target}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
